import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Mail, Eye, Edit3, Send, CheckCircle, XCircle, Loader2,
  Globe, Power, AlertTriangle, RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LANGUAGES = [
  { id: 'es', label: 'Español', flag: 'ES' },
  { id: 'en', label: 'English', flag: 'EN' },
  { id: 'pt', label: 'Português', flag: 'PT' },
];

const WelcomeEmailPanel = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [previewLang, setPreviewLang] = useState('es');
  const [editLang, setEditLang] = useState('es');
  const [mode, setMode] = useState('preview'); // 'preview' | 'edit'
  const [testEmail, setTestEmail] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [saveResult, setSaveResult] = useState(null);

  // Editable drafts per language
  const [drafts, setDrafts] = useState({ es: {}, en: {}, pt: {} });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/welcome-email-settings`, { withCredentials: true });
      setSettings(r.data);
      // Initialize drafts from current templates
      const t = r.data.templates || {};
      setDrafts({
        es: { subject: t.es?.subject || '', body: t.es?.body || '' },
        en: { subject: t.en?.subject || '', body: t.en?.body || '' },
        pt: { subject: t.pt?.subject || '', body: t.pt?.body || '' },
      });
      setHasUnsavedChanges(false);
    } catch (e) {
      console.error('Failed to load welcome email settings', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const handleDraftChange = (lang, field, value) => {
    setDrafts(prev => ({
      ...prev,
      [lang]: { ...prev[lang], [field]: value }
    }));
    setHasUnsavedChanges(true);
    setSaveResult(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveResult(null);
    try {
      await axios.put(`${API_URL}/api/admin/welcome-email-settings`, {
        templates: drafts,
        sender_type: settings?.sender_type || 'notification',
      }, { withCredentials: true });
      setSaveResult({ type: 'success', message: 'Templates saved successfully.' });
      setHasUnsavedChanges(false);
      await loadSettings();
    } catch (e) {
      setSaveResult({ type: 'error', message: e.response?.data?.detail || 'Failed to save.' });
    }
    setSaving(false);
  };

  const handleSenderChange = async (newSender) => {
    try {
      await axios.put(`${API_URL}/api/admin/welcome-email-settings`, {
        sender_type: newSender,
      }, { withCredentials: true });
      setSettings(prev => ({ ...prev, sender_type: newSender }));
    } catch {}
  };

  const handleResetDefaults = async () => {
    setSaving(true);
    setSaveResult(null);
    try {
      await axios.post(`${API_URL}/api/admin/welcome-email-reset`, {}, { withCredentials: true });
      setSaveResult({ type: 'success', message: 'Templates reset to defaults.' });
      setHasUnsavedChanges(false);
      await loadSettings();
    } catch (e) {
      setSaveResult({ type: 'error', message: e.response?.data?.detail || 'Reset failed.' });
    }
    setSaving(false);
  };

  const handleActivate = async (active) => {
    setActivating(true);
    setSaveResult(null);
    try {
      const r = await axios.post(`${API_URL}/api/admin/welcome-email-activate`, { active }, { withCredentials: true });
      setSettings(prev => ({ ...prev, active: r.data.active, approved_at: active ? new Date().toISOString() : null }));
      setSaveResult({ type: 'success', message: active ? 'Welcome email system ACTIVATED.' : 'Welcome email system deactivated.' });
    } catch (e) {
      setSaveResult({ type: 'error', message: e.response?.data?.detail || 'Failed to toggle.' });
    }
    setActivating(false);
  };

  const handleSendTest = async () => {
    if (!testEmail.trim()) return;
    setSendingTest(true);
    setTestResult(null);
    try {
      await axios.post(`${API_URL}/api/admin/welcome-email-test`, {
        email: testEmail.trim(),
        language: previewLang,
        name: 'Admin Test',
      }, { withCredentials: true });
      setTestResult({ type: 'success', message: `Test email sent to ${testEmail} (${previewLang.toUpperCase()})` });
    } catch (e) {
      setTestResult({ type: 'error', message: e.response?.data?.detail || 'Failed to send test.' });
    }
    setSendingTest(false);
  };

  const renderPreviewHTML = (lang) => {
    const t = drafts[lang] || {};
    const body = (t.body || '')
      .replace(/\{\{name\}\}/g, 'Admin Test')
      .replace(/\{\{email\}\}/g, 'admin@ogmusic.com')
      .replace(/\{\{site_url\}\}/g, API_URL || '#');
    return body;
  };

  const renderPreviewSubject = (lang) => {
    const t = drafts[lang] || {};
    return (t.subject || '').replace(/\{\{name\}\}/g, 'Admin Test');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-white/40" />
      </div>
    );
  }

  const isActive = settings?.active;

  return (
    <div data-testid="welcome-email-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">Welcome Email System</h2>
          <p className="text-sm text-white/40 mt-1">
            Configure, preview, and activate automatic welcome emails for new verified users
          </p>
        </div>
        <button onClick={loadSettings} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors" data-testid="welcome-refresh">
          <RefreshCw className="w-4 h-4 text-white/50" />
        </button>
      </div>

      {/* Status Banner */}
      <div className={`p-4 rounded-xl mb-6 flex items-center gap-3 ${
        isActive
          ? 'bg-green-500/10 border border-green-500/20'
          : 'bg-yellow-500/10 border border-yellow-500/20'
      }`} data-testid="welcome-status-banner">
        {isActive ? (
          <CheckCircle className="w-5 h-5 text-green-400 shrink-0" />
        ) : (
          <AlertTriangle className="w-5 h-5 text-yellow-400 shrink-0" />
        )}
        <div className="flex-1">
          <p className={`text-sm font-medium ${isActive ? 'text-green-400' : 'text-yellow-400'}`}>
            {isActive ? 'System Active — Welcome emails are being sent automatically' : 'System Inactive — No welcome emails are being sent'}
          </p>
          {settings?.approved_at && (
            <p className="text-xs text-white/30 mt-0.5">
              Activated: {new Date(settings.approved_at).toLocaleString()}
            </p>
          )}
        </div>
        <button
          onClick={() => handleActivate(!isActive)}
          disabled={activating || (hasUnsavedChanges && !isActive)}
          className={`px-4 py-2 rounded-lg text-xs font-semibold flex items-center gap-2 transition-all ${
            isActive
              ? 'bg-red-500/15 border border-red-500/25 text-red-400 hover:bg-red-500/25'
              : 'bg-green-500/15 border border-green-500/25 text-green-400 hover:bg-green-500/25'
          } disabled:opacity-40`}
          data-testid="welcome-activate-toggle"
        >
          {activating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Power className="w-3 h-3" />}
          {isActive ? 'Deactivate' : 'Activate'}
        </button>
      </div>

      {hasUnsavedChanges && !isActive && (
        <div className="p-3 rounded-xl mb-4 bg-orange-500/10 border border-orange-500/20 text-xs text-orange-400">
          Save your changes before activating the system.
        </div>
      )}

      {/* Mode Toggle + Language Selector */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="flex rounded-lg overflow-hidden border border-white/10">
          <button
            onClick={() => setMode('preview')}
            className={`px-4 py-2 text-xs font-medium flex items-center gap-1.5 transition-all ${mode === 'preview' ? 'bg-[#E8720C] text-white' : 'bg-white/5 text-white/50 hover:text-white/70'}`}
            data-testid="welcome-mode-preview"
          >
            <Eye className="w-3 h-3" /> Preview
          </button>
          <button
            onClick={() => setMode('edit')}
            className={`px-4 py-2 text-xs font-medium flex items-center gap-1.5 transition-all ${mode === 'edit' ? 'bg-[#E8720C] text-white' : 'bg-white/5 text-white/50 hover:text-white/70'}`}
            data-testid="welcome-mode-edit"
          >
            <Edit3 className="w-3 h-3" /> Edit
          </button>
        </div>

        <div className="flex gap-1.5">
          {LANGUAGES.map(lang => {
            const activeLang = mode === 'preview' ? previewLang : editLang;
            return (
              <button
                key={lang.id}
                onClick={() => mode === 'preview' ? setPreviewLang(lang.id) : setEditLang(lang.id)}
                className={`px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all ${
                  activeLang === lang.id
                    ? 'bg-[#E8720C]/15 border border-[#E8720C]/30 text-[#E8720C]'
                    : 'bg-white/5 border border-white/10 text-white/40 hover:text-white/60'
                }`}
                data-testid={`welcome-lang-${lang.id}`}
              >
                <Globe className="w-3 h-3" />
                {lang.flag}
              </button>
            );
          })}
        </div>

        {mode === 'edit' && (
          <div className="ml-auto flex gap-2">
            <button
              onClick={handleResetDefaults}
              disabled={saving}
              className="px-4 py-2 rounded-lg text-xs font-medium bg-white/5 border border-white/10 text-white/50 hover:text-white/70 disabled:opacity-40 flex items-center gap-2 transition-all"
              data-testid="welcome-reset-btn"
            >
              <RefreshCw className="w-3 h-3" />
              Reset Defaults
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !hasUnsavedChanges}
              className="px-5 py-2 rounded-lg text-xs font-semibold bg-[#E8720C] text-white hover:bg-[#E8720C]/90 disabled:opacity-40 flex items-center gap-2 transition-all"
              data-testid="welcome-save-btn"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
              Save Templates
            </button>
          </div>
        )}
      </div>

      {/* Result Messages */}
      {saveResult && (
        <div className={`p-3 rounded-xl mb-4 text-sm ${
          saveResult.type === 'success' ? 'bg-green-500/10 border border-green-500/20 text-green-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'
        }`} data-testid="welcome-save-result">
          {saveResult.message}
        </div>
      )}

      {/* PREVIEW MODE */}
      {mode === 'preview' && (
        <div className="space-y-4">
          {/* Subject Preview */}
          <div className="glass-card p-4">
            <label className="block text-[10px] text-white/30 uppercase tracking-wider mb-2">Subject Line ({previewLang.toUpperCase()})</label>
            <p className="text-sm font-medium text-white/90" data-testid="welcome-preview-subject">
              {renderPreviewSubject(previewLang) || 'No subject configured'}
            </p>
          </div>

          {/* Email Body Preview — Simulated inbox */}
          <div className="glass-card overflow-hidden">
            <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#E8720C]" />
              <span className="text-xs text-white/60">Email Preview — {LANGUAGES.find(l => l.id === previewLang)?.label}</span>
            </div>
            <div className="p-6" style={{ background: '#0d0d10' }}>
              {/* Simulated email container */}
              <div style={{
                maxWidth: '600px',
                margin: '0 auto',
                background: '#111114',
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.06)',
                overflow: 'hidden',
              }}>
                {/* Logo header area */}
                <div style={{ textAlign: 'center', padding: '24px 24px 8px' }}>
                  <img
                    src={`${API_URL}/api/uploads/og_logo.png`}
                    alt="OG MUSIC GROUP"
                    style={{ height: '40px', margin: '0 auto' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
                {/* Email body content */}
                <div
                  style={{ padding: '8px 24px 24px', color: '#ccc', fontSize: '14px' }}
                  dangerouslySetInnerHTML={{ __html: renderPreviewHTML(previewLang) }}
                  data-testid="welcome-preview-body"
                />
                {/* Footer */}
                <div style={{ padding: '16px 24px', borderTop: '1px solid rgba(255,255,255,0.04)', textAlign: 'center' }}>
                  <p style={{ color: '#555', fontSize: '10px', margin: 0 }}>OG MUSIC GROUP &copy; {new Date().getFullYear()}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* EDIT MODE */}
      {mode === 'edit' && (
        <div className="space-y-4">
          {/* Subject Editor */}
          <div className="glass-card p-4">
            <label className="block text-[10px] text-white/30 uppercase tracking-wider mb-2">
              Subject Line — {LANGUAGES.find(l => l.id === editLang)?.label}
            </label>
            <input
              value={drafts[editLang]?.subject || ''}
              onChange={(e) => handleDraftChange(editLang, 'subject', e.target.value)}
              className="w-full input-glass text-sm"
              placeholder={`Subject for ${editLang.toUpperCase()} version...`}
              data-testid="welcome-edit-subject"
            />
            <p className="text-[10px] text-white/25 mt-1.5">
              Variables: {'{{name}}'} {'{{email}}'} {'{{site_url}}'}
            </p>
          </div>

          {/* Body Editor */}
          <div className="glass-card p-4">
            <label className="block text-[10px] text-white/30 uppercase tracking-wider mb-2">
              Email Body (HTML) — {LANGUAGES.find(l => l.id === editLang)?.label}
            </label>
            <textarea
              value={drafts[editLang]?.body || ''}
              onChange={(e) => handleDraftChange(editLang, 'body', e.target.value)}
              className="w-full input-glass min-h-[300px] text-xs font-mono leading-relaxed"
              placeholder="<div>Your welcome email HTML...</div>"
              data-testid="welcome-edit-body"
            />
          </div>

          {/* Live Preview While Editing */}
          <div className="glass-card overflow-hidden">
            <div className="px-4 py-3 border-b border-white/5">
              <span className="text-xs text-white/40">Live Preview</span>
            </div>
            <div className="p-6" style={{ background: '#0d0d10' }}>
              <div style={{
                maxWidth: '600px',
                margin: '0 auto',
                background: '#111114',
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.06)',
                padding: '24px',
              }}>
                <div
                  style={{ color: '#ccc', fontSize: '14px' }}
                  dangerouslySetInnerHTML={{ __html: renderPreviewHTML(editLang) }}
                  data-testid="welcome-edit-preview"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sender Selection */}
      <div className="glass-card p-4 mt-6">
        <label className="block text-[10px] text-white/30 uppercase tracking-wider mb-3">Sender Email</label>
        <div className="flex gap-2">
          {['notification', 'delivery'].map(s => (
            <button
              key={s}
              onClick={() => handleSenderChange(s)}
              className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                settings?.sender_type === s
                  ? 'bg-[#E8720C] text-white'
                  : 'bg-white/5 text-white/50 border border-white/10 hover:border-white/20'
              }`}
              data-testid={`welcome-sender-${s}`}
            >
              {s === 'notification' ? 'Support Email' : 'Delivery Email'}
            </button>
          ))}
        </div>
      </div>

      {/* Test Email */}
      <div className="glass-card p-4 mt-4">
        <label className="block text-[10px] text-white/30 uppercase tracking-wider mb-3">
          Send Test Email ({previewLang.toUpperCase()} version)
        </label>
        <div className="flex gap-2">
          <input
            value={testEmail}
            onChange={(e) => { setTestEmail(e.target.value); setTestResult(null); }}
            className="flex-1 input-glass text-xs"
            placeholder="your@email.com"
            data-testid="welcome-test-email-input"
          />
          <button
            onClick={handleSendTest}
            disabled={sendingTest || !testEmail.trim()}
            className="px-5 py-2 rounded-lg text-xs font-semibold bg-[#E8720C]/15 border border-[#E8720C]/30 text-[#E8720C] hover:bg-[#E8720C]/25 disabled:opacity-40 flex items-center gap-2 transition-all"
            data-testid="welcome-send-test-btn"
          >
            {sendingTest ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            Send Test
          </button>
        </div>
        {testResult && (
          <div className={`mt-3 p-3 rounded-lg text-xs ${
            testResult.type === 'success' ? 'bg-green-500/10 border border-green-500/20 text-green-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'
          }`} data-testid="welcome-test-result">
            {testResult.type === 'success' ? <CheckCircle className="w-3 h-3 inline mr-1" /> : <XCircle className="w-3 h-3 inline mr-1" />}
            {testResult.message}
          </div>
        )}
      </div>
    </div>
  );
};

export default WelcomeEmailPanel;
